import { type api, useAccessMutation } from "@/services/api";
import type { MutationHookResult } from "@Light/services/queryTypes";
import { createContext, useContext, useEffect, useRef } from "react";

export type AccessState = [
  ReturnType<typeof useAccessMutation>[0],
  MutationHookResult<typeof api.endpoints.access.Types.MutationDefinition>,
];

export const AccessContext = createContext<AccessState | undefined>(undefined);

export type WithAccessProps = {
  children: React.ReactNode;
};

export const WithAccess: React.FC<WithAccessProps> = ({
  children,
}: WithAccessProps) => {
  const [trigger, result] = useAccessMutation();
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) {
      return;
    }

    triggeredRef.current = true;
    trigger();
  }, [trigger]);

  if (result.isLoading) {
    return null;
  }

  return (
    <AccessContext.Provider value={[trigger, result]}>
      {children}
    </AccessContext.Provider>
  );
};

export function useAccess() {
  const ctx = useContext(AccessContext);
  if (!ctx) {
    return undefined;
  }
  const [, result] = ctx;

  if (!result?.isSuccess || result.error) {
    return undefined;
  }

  return result.data;
}

export function useRequiredAccess() {
  const access = useAccess();
  if (!access) {
    throw new Error("access required for use with useRequiredAccess");
  }

  return access;
}
